import { queryOptions } from "@tanstack/react-query";

import { apiUrl } from "@workspace/base-api/apiUrl";
import { fetchJson, HttpRequestError } from "@workspace/base-api/fetchJson";
import { toCamel } from "@workspace/shared";
import { venueStaffManagementApi } from "@workspace/venue-management-staff";
import type { StaffSettings } from "@workspace/venue-management-staff/types";

import type { ApiResponse } from "./hooks/venue-dashboard-context/types";

export interface VenueSettingsResponse {
	staff: StaffSettings;
	paymentProvider?: unknown;
}

export const venueManagementApi = {
	...venueStaffManagementApi,
	async getDashboard(
		tenant: string,
		venueId: string,
		section: string = "",
		start: Date | null = null,
		end: Date | null = null,
	) {
		const s = section.length > 0 ? `/${section}` : "";
		const url = new URL(`/venue-management/${venueId}/dashboard${s}`, apiUrl);

		const body: Record<string, string> = {};
		if (start) {
			body.start = start.toISOString();
		}
		if (end) {
			body.end = end.toISOString();
		}

		try {
			const response = await fetchJson<ApiResponse>(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify(body),
			});

			return response;
		} catch (e: unknown) {
			const error = e as Error;
			if (error.name === "HttpRequestError") {
				const httpError = error as { response?: Response };
				throw new HttpRequestError(
					`Failed to fetch dashboard data tenant: ${tenant} venue: ${venueId} ${section}`,
					httpError.response!,
				);
			}
			throw error;
		}
	},
	getDashboardOptions: (
		tenant: string,
		venueId: string,
		section: string = "",
		start: Date | null = null,
		end: Date | null = null,
	) => {
		return queryOptions({
			staleTime: 5_000,
			queryKey: [
				tenant,
				"venue-management",
				"dashboard",
				venueId,
				section,
				start,
				end,
			],
			queryFn: () =>
				venueManagementApi.getDashboard(tenant, venueId, section, start, end),
		});
	},
	async syncScreensWithExternal(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/screens/sync`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	syncScreensWithExternalOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "screens", "sync"],
			mutationFn: (data: Record<string, unknown>) =>
				venueManagementApi.syncScreensWithExternal(tenant, venueId, data),
		};
	},
	async addMedia(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/screens/media`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	addMediaOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "media", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueManagementApi.addMedia(tenant, venueId, data),
		};
	},
	async getSettings(tenant: string, venueId: string) {
		const url = new URL(`/venue-management/${venueId}/settings`, apiUrl);

		return await fetchJson<VenueSettingsResponse>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getSettingsOptions: (tenant: string, venueId: string) => {
		return queryOptions({
			queryKey: [tenant, venueId, "settings"],
			queryFn: () => venueManagementApi.getSettings(tenant, venueId),
		});
	},
	getVenueRolesOptions: (tenant: string, venueId: string) => {
		return queryOptions({
			queryKey: [tenant, venueId, "venue-roles"],
			queryFn: async () => {
				const settings = await venueManagementApi.getSettings(tenant, venueId);
				return settings.staff.staffRoles;
			},
			enabled: !!venueId,
		});
	},
};
